import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Shield, Sparkles } from "lucide-react";
import {
  BrixBtn,
  BrixPageHero,
  BrixPageShell,
  BrixPanel,
} from "../../components/layout/BrixPageShell";
import { fetchOrgAuditLogs } from "../../services/auditService";

function formatAuditDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatEntityName(entity) {
  if (!entity) return "—";
  return String(entity)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchOrgAuditLogs({ limit: 300, q: debouncedSearch || undefined });
      setLogs(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setLogs([]);
      setTotal(0);
      setError(err?.response?.data?.detail || err?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    const count = debouncedSearch ? total : logs.length;
    return `${count} record${count === 1 ? "" : "s"}`;
  }, [loading, debouncedSearch, total, logs.length]);

  return (
    <BrixPageShell>
      <BrixPageHero
        icon={Shield}
        eyebrow={
          <>
            <Sparkles size={10} />
            Compliance
          </>
        }
        title="Audit Log Viewer"
        subtitle="Track user actions, entity changes, and security events across the organisation."
        actions={
          <div className="relative w-full min-w-[220px] max-w-sm">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="search"
              placeholder="Search by user, action, entity…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full min-h-[40px] pl-9 pr-3 rounded-[10px] border border-white/25 bg-white/95 text-sm font-medium text-slate-900"
            />
          </div>
        }
      />

      {error && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          {typeof error === "string" ? error : "Could not load audit logs — please refresh"}
        </div>
      )}

      <BrixPanel title="Audit trail" subtitle={subtitle} noPadding>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>IP Address</th>
                <th>Date & Time</th>
                <th className="text-center">View</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500 font-medium">
                    Loading audit trail…
                  </td>
                </tr>
              )}
              {!loading &&
                logs.map((log) => (
                  <tr key={log.log_id}>
                    <td>{log.user || "—"}</td>
                    <td>{log.action}</td>
                    <td>{formatEntityName(log.entity)}</td>
                    <td className="font-mono text-xs">{log.ip_address || "—"}</td>
                    <td>{formatAuditDateTime(log.occurred_at)}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        onClick={() => setSelectedLog(log)}
                        className="text-sm font-semibold text-blue-700 hover:underline"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500 font-medium">
                    No audit records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </BrixPanel>

      {selectedLog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="brix-panel w-full max-w-lg p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Audit Details</h2>
            <div className="space-y-2 text-sm font-medium text-slate-700">
              <p>
                <strong>User:</strong> {selectedLog.user || "—"}
              </p>
              <p>
                <strong>Action:</strong> {selectedLog.action}
              </p>
              <p>
                <strong>Entity:</strong> {formatEntityName(selectedLog.entity)}
              </p>
              <p>
                <strong>Source:</strong> {selectedLog.source}
              </p>
              <p>
                <strong>IP Address:</strong> {selectedLog.ip_address || "—"}
              </p>
              <p>
                <strong>Date:</strong> {formatAuditDateTime(selectedLog.occurred_at)}
              </p>
              <div>
                <strong>Changes:</strong>
                <pre className="bg-slate-100 p-3 mt-2 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(selectedLog.details || {}, null, 2)}
                </pre>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <BrixBtn type="button" variant="secondary" onClick={() => setSelectedLog(null)}>
                Close
              </BrixBtn>
            </div>
          </div>
        </div>
      )}
    </BrixPageShell>
  );
};

export default AuditLogViewer;
